import { useEffect, useState } from "react";
import type { CallHistory } from "../../shared/types";
import { useNow } from "../ui/use-now";

const week = 7 * 24 * 60 * 60_000;

// Only when the latest missed call was seen is saved here, for this browser's badge.
const seenKey = (id: string) => `ranna:missed-calls-seen:v1:${id}`;

// Being rung and never connected is a missed call however the ring stopped,
// unless the person being rung turned it down.
export const isMissedCall = (call: CallHistory, me: string) =>
  call.callee === me &&
  !call.connected_at &&
  ["missed", "cancelled", "disconnected"].includes(call.status);

// One line per person, from the past week, until a newer call with them.
// History comes newest first.
export const recentMissedCalls = (
  history: CallHistory[],
  me: string,
  now: number,
) =>
  history.filter(
    (call, index) =>
      isMissedCall(call, me) &&
      call.created_at > now - week &&
      history.findIndex((other) => other.person_id === call.person_id) ===
        index,
  );

export function useMissedCalls(
  id: string | undefined,
  history: CallHistory[],
  viewing: boolean,
) {
  const now = useNow();
  const [seen, setSeen] = useState<{ id?: string; at: number }>({ at: 0 });
  useEffect(() => {
    if (!id) return;
    const read = () => {
      let at = 0;
      try {
        at = Number(localStorage.getItem(seenKey(id))) || 0;
      } catch {}
      setSeen({ id, at });
    };
    read();
    const sync = (event: StorageEvent) => {
      if (event.key === seenKey(id) || event.key === null) read();
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [id]);
  const calls = id ? recentMissedCalls(history, id, now) : [];
  const latest = calls[0]?.created_at ?? 0;
  useEffect(() => {
    if (!id || !viewing || seen.id !== id || latest <= seen.at) return;
    setSeen({ id, at: latest });
    try {
      localStorage.setItem(seenKey(id), String(latest));
    } catch {}
  }, [id, viewing, latest, seen]);
  return seen.id === id
    ? calls.filter((call) => call.created_at > seen.at).length
    : 0;
}
